import type { ReactElement } from 'react';
import type { RuleSet } from 'styled-components';

import { useState, useEffect } from 'react';
import styled, { css } from 'styled-components';

import { useUserContext } from '../../../../utilities/context/dynamic/UserContext';
import { useAppContext } from '../../../../utilities/context/static/AppContext';
import { useStaticContext } from '../../../../utilities/context/static/StaticContext';
import { getPpeJourneyContent } from '../../../../utilities/helpers/getPpeJourneyContent';
import { useEvaluatePersonalisedDiscount } from '../../../../utilities/hooks/useEvaluatePersonalisedDiscount';
import { SiteType } from '~/utilities/graphql/codegen';

const PpeTextColorConfig: Record<SiteType, RuleSet<object>> = {
  [SiteType.Gstar]: css``,
  [SiteType.Outlet]: css`
    color: ${({ theme }) => theme.colors.content['sale-alt']};

    & > *:first-child,
    & > p:nth-of-type(2) {
      color: ${({ theme }) => theme.colors.content['sale-alt']};
    }
  `,
  [SiteType.Employeeshop]: css``,
  [SiteType.Aaf]: css``,
  [SiteType.Wahts]: css``,
};

const S = {
  PpeTextWrapper: styled.div`
    display: flex;
    flex-wrap: wrap;
    text-align: left;
    column-gap: 4px;
    font-size: ${({ theme }) => theme.font.size['1']};

    & > *:first-child {
      color: ${({ theme }) => theme.colors.content.sale};
    }

    & > p {
      font-size: ${({ theme }) => theme.font.size[1]};
    }

    & > p:nth-of-type(2) {
      color: ${({ theme }) => theme.colors.content.tertiary};
    }

    ${({ theme }) => PpeTextColorConfig[theme.siteType]}
  `,
};

interface PpeMessageProps {
  hasDiscountPrice?: boolean | null;
  salesDiscountsEndDate?: string | null;
  productCode?: string | null;
}

export const PpeMessage = ({
  hasDiscountPrice,
  salesDiscountsEndDate,
  productCode,
}: PpeMessageProps): ReactElement => {
  const { user } = useUserContext();
  const { language } = useAppContext();
  const { ppeContent } = useStaticContext();
  const [ppeMessage, setPpeMessage] = useState<{
    pdpText1: string;
    pdpText2: string;
  }>({ pdpText1: '', pdpText2: '' });

  const { isValidDiscount, isLoading } = useEvaluatePersonalisedDiscount({ productCode });

  useEffect(() => {
    if (!user || !language || !isValidDiscount || isLoading) {
      return;
    }

    const handlePpeJourney = (language: string) => {
      const { expirationDate, formattedValue, trafficSource, applicableToDiscountedProducts } =
        user.personalisedDiscount ?? {};

      if (
        !expirationDate ||
        (!applicableToDiscountedProducts && hasDiscountPrice) ||
        salesDiscountsEndDate ||
        ppeMessage.pdpText1
      ) {
        return;
      }

      const ppeJourneyContent = getPpeJourneyContent({
        ppeContent,
        trafficSource,
        expirationDate,
        formattedValue,
        language,
      });

      if (ppeJourneyContent?.pdpText1) {
        const ppeMessage = { pdpText1: ppeJourneyContent.pdpText1.html as string, pdpText2: '' };

        if (ppeJourneyContent?.pdpText2) {
          ppeMessage.pdpText2 = ppeJourneyContent.pdpText2.html as string;
        }

        setPpeMessage(ppeMessage);
      }
    };

    handlePpeJourney(language);
  }, [
    user,
    language,
    ppeMessage.pdpText1,
    ppeMessage.pdpText2,
    salesDiscountsEndDate,
    hasDiscountPrice,
    ppeContent,
    isValidDiscount,
    isLoading,
  ]);

  return ppeMessage.pdpText1 ? (
    <S.PpeTextWrapper
      className="ppeMessageWrapper"
      dangerouslySetInnerHTML={{ __html: ppeMessage.pdpText1 + ppeMessage.pdpText2 }}
    />
  ) : (
    <></>
  );
};
